import {
  INFLUENCERS,
  INFLUENCER_BY_ID,
  INFLUENCER_COMMENT_CONVERSATIONS,
  INFLUENCER_POST_CONVERSATIONS,
  INFLUENCER_REPLY_CONVERSATIONS,
  INDUSTRY_LABELS,
  INDUSTRY_VALUES,
  SAFETY_BANDS,
  SAFETY_BAND_LABELS,
  type InfluencerProfile,
  type MentionRow,
} from './generator';
import { metrics } from './metrics';

/**
 * Everything the three Influencers screens read, and the only place any of it
 * is computed.
 *
 * The module is unusual in this app in having **three** screens over one
 * corpus — a list, one creator, one creator's industry — and the figures have
 * to agree across all three: the `Brand safety` a creator shows in the list is
 * the figure in the middle of their donut, and the `Total posts` an industry
 * reports has to sum to the creator's own. Deriving each screen separately is
 * how that stops being true, so every screen calls into this file and none of
 * them counts a row itself.
 *
 * **Scoping is one step, not two.** `influencerRows` keeps the rows a tracked
 * creator posted, and nothing else happens first. That step is this module's
 * equivalent of Media's `coverage()` and exists for the same reason: `pivot`
 * skips a null category on its own, but `total` reduces the rows *as handed
 * in*, so passing the unattributed three quarters of the corpus would print
 * denominators far larger than the columns above them sum to.
 *
 * **No dimension filtering happens here at all**, and that is deliberate rather
 * than an omission. None of the three frames draws a Filters Bar, so the module
 * renders no chips — and `docs/rules.md` §0 is explicit that a module must
 * never filter by a chip it does not render. The date range is the only control
 * these screens answer to.
 */

/** The em dash a figure falls back to when the period cannot answer it. */
const NONE = '—';

const POST_SHAPES = new Set(INFLUENCER_POST_CONVERSATIONS);
const COMMENT_SHAPES = new Set(INFLUENCER_COMMENT_CONVERSATIONS);
const REPLY_SHAPES = new Set(INFLUENCER_REPLY_CONVERSATIONS);

/**
 * Rows a tracked creator posted — the module's only scoping step.
 *
 * **Influencers does not call `ownBrand`, and it is the second module that
 * does not** — after Competitors, and for a related but distinct reason.
 *
 * Competitors is unscoped because brand is a dimension the user filters on
 * there. This one is unscoped because **brand safety is a judgement about the
 * creator, not about us**. Vetting someone means looking at the whole of their
 * tracked output: a creator whose Robi posts are the risky ones is exactly as
 * risky to stand next to, and a module that could not see them would rate them
 * safe on the strength of the handful of posts that happened to name
 * Grameenphone. The figures here are therefore this creator's tracked activity,
 * which is what the help text on each card says.
 *
 * It is also what makes the module readable, and that was measured rather than
 * assumed: scoped to our own brand the busiest creator has 18 rows and 5 posts
 * in the default window and the median has 6 and 1, which draws an industry
 * table of `0 / 3 / 0` and a donut with an empty band. Unscoped those become 47
 * and 13. Settled with the user (2026-09-17) and recorded in `docs/rules.md`
 * §0 beside the Competitors exception.
 *
 * The corollary Competitors documents does **not** apply here: no chip lets the
 * user deselect a brand on this screen, so no figure can lose its subject the
 * way "our share" can there.
 */
export function influencerRows(rows: readonly MentionRow[]): readonly MentionRow[] {
  return rows.filter((row) => row.influencer !== null);
}

/** Those of them belonging to one creator. */
export function rowsFor(rows: readonly MentionRow[], id: string): readonly MentionRow[] {
  return rows.filter((row) => row.influencer === id);
}

/** …and narrowed again to one industry, which is the third screen's whole slice. */
export function rowsForIndustry(
  rows: readonly MentionRow[],
  id: string,
  industry: string,
): readonly MentionRow[] {
  return rows.filter((row) => row.influencer === id && row.industry === industry);
}

export function profileFor(id: string): InfluencerProfile | undefined {
  return INFLUENCER_BY_ID[id];
}

export function industryLabel(value: string): string {
  return INDUSTRY_LABELS[value] ?? value;
}

export function isIndustry(value: string): boolean {
  return (INDUSTRY_VALUES as readonly string[]).includes(value);
}

/* ------------------------------------------------------------------ counts */

/**
 * The three column sets the industry table asks for, counted over disjoint
 * conversation shapes.
 *
 * `Total posts`, `Total comments` and `Total replies` are all **row counts**
 * here rather than engagement sums, and that needs saying because the
 * `Platform analytics` table one card up uses the word `Comments` for the
 * engagement column instead. Neither reading works for both tables: this corpus
 * has no `replies` engagement column and never can have one — the four types
 * are guaranteed to sum to `engagement` exactly, so a fifth would either break
 * that invariant or resplit it and move every engagement figure in every module
 * already built. Conversation shape is the only source that answers all three
 * labels without inventing a field, and the shapes are disjoint by assertion,
 * so the three columns cannot double-count a row.
 *
 * The collision on the word `Comments` is real and is flagged for the designer
 * in `docs/rules.md` §7 rather than resolved unilaterally.
 */
export const counts = {
  posts: (rows: readonly MentionRow[]) => rows.filter((r) => POST_SHAPES.has(r.conversation)).length,
  comments: (rows: readonly MentionRow[]) =>
    rows.filter((r) => COMMENT_SHAPES.has(r.conversation)).length,
  replies: (rows: readonly MentionRow[]) =>
    rows.filter((r) => REPLY_SHAPES.has(r.conversation)).length,
};

/** Share of a slice's rows sitting in the `safe` band, 0–100. Empty → `null`. */
export function safeShare(rows: readonly MentionRow[]): number | null {
  if (rows.length === 0) return null;
  let safe = 0;
  for (const row of rows) if (row.safetyBand === 'safe') safe += 1;
  return (safe / rows.length) * 100;
}

/* -------------------------------------------------------------------- list */

export type InfluencerListRow = {
  id: string;
  name: string;
  platforms: readonly string[];
  followers: number;
  /** Rows in the period that are posts rather than responses. */
  posts: number;
  /** Every row in the period, posts and responses alike. */
  activity: number;
  /** `engagementRate` over the creator's rows, or `null` on an empty period. */
  engagementRate: number | null;
  /** The creator's standing score — stored, so it does not move with the date. */
  safety: number;
};

/**
 * One row per tracked creator, ranked by following.
 *
 * **Every creator appears, including the ones with nothing in the period.** The
 * roster is a roster, not a result set: a creator who posted nothing this week
 * has not stopped existing, and dropping them would make the list's length move
 * with the date picker while the pagination stayed put. They report `0` posts
 * and an em dash for the rate, which says "nothing here" rather than "0.0%",
 * which would claim they posted and got no engagement.
 */
export function influencerList(rows: readonly MentionRow[]): InfluencerListRow[] {
  const byCreator = new Map<string, MentionRow[]>();
  for (const row of rows) {
    if (row.influencer === null) continue;
    const list = byCreator.get(row.influencer);
    if (list) list.push(row);
    else byCreator.set(row.influencer, [row]);
  }

  return INFLUENCERS.map((profile) => {
    const own = byCreator.get(profile.id) ?? [];
    return {
      id: profile.id,
      name: profile.name,
      platforms: profile.platforms,
      followers: profile.followers,
      posts: counts.posts(own),
      activity: own.length,
      engagementRate: own.length === 0 ? null : metrics.engagementRate.reduce(own),
      safety: profile.safety,
    };
  });
}

/* ---------------------------------------------------------------- platform */

export type PlatformRow = {
  channel: string;
  followers: number;
  posts: number;
  engagementRate: number | null;
  reactions: number;
  comments: number;
  /** Safe share of this channel's rows — derived, unlike the creator's own score. */
  safeShare: number | null;
};

/**
 * The `Platform analytics` table — one row per channel the creator is on.
 *
 * **Followers are the creator's, repeated per channel, and that is a known
 * limitation of the fixture.** The roster carries one follower count per
 * person, not one per platform, because the frame's own table shows a single
 * figure (`1.28M`) against a single-platform creator and says nothing about how
 * it would split. Repeating it is the honest version of not knowing; splitting
 * it by some invented ratio would be a number nobody chose. Flagged for the
 * designer.
 *
 * Every channel the creator is on gets a row even with nothing in the period,
 * for `influencerList`'s reason — a platform they are on is a fact about them,
 * not about the week.
 */
export function platformRows(rows: readonly MentionRow[], id: string): PlatformRow[] {
  const profile = profileFor(id);
  if (!profile) return [];
  const own = rowsFor(rows, id);

  return profile.platforms.map((channel) => {
    const here = own.filter((row) => row.channel === channel);
    let reactions = 0;
    let comments = 0;
    for (const row of here) {
      reactions += row.reactions;
      comments += row.comments;
    }
    return {
      channel,
      followers: profile.followers,
      posts: counts.posts(here),
      engagementRate: here.length === 0 ? null : metrics.engagementRate.reduce(here),
      reactions,
      comments,
      safeShare: safeShare(here),
    };
  });
}

/* ---------------------------------------------------------------- industry */

export type IndustryRow = {
  industry: string;
  label: string;
  posts: number;
  comments: number;
  replies: number;
  /**
   * Safe share of this industry's rows, 0–100, or `null` when the creator
   * posted nothing in it this period.
   *
   * **Derived here, where the creator's own score is stored**, and the
   * asymmetry is deliberate. There is no stored score for a
   * creator-and-industry pair and inventing one would be a second brand-safety
   * number that could disagree with the first. Because `drawSafetyBand` draws
   * each post against the creator's score, these shares scatter around it
   * rather than contradicting it.
   */
  safeShare: number | null;
};

/** The `Industry specific split` table, ranked by posts. */
export function industryRows(rows: readonly MentionRow[], id: string): IndustryRow[] {
  const own = rowsFor(rows, id);

  return INDUSTRY_VALUES.map((industry) => {
    const here = own.filter((row) => row.industry === industry);
    return {
      industry,
      label: industryLabel(industry),
      posts: counts.posts(here),
      comments: counts.comments(here),
      replies: counts.replies(here),
      safeShare: safeShare(here),
    };
  })
    /*
     * Ranked, and by *activity* rather than by the `posts` column. Ranking by
     * the visible column would put an industry the creator only ever replied in
     * below one they never touched at all, both reading `0` — see `activity`.
     */
    .sort((a, b) => b.posts + b.comments + b.replies - (a.posts + a.comments + a.replies));
}

/* ------------------------------------------------------------------ donuts */

/** `{ name, value }` rows, which is the shape every donut on these screens takes. */
export type SliceRow = { name: string; value: number };

/**
 * The `Brand safety score` ring — a creator's (or an industry's) posts banded
 * safe, needs-review, risky.
 *
 * Always three slices in roster order, including zeroes, because the bands are
 * a *scale*: a ring that silently drops `Risky` when there is none looks the
 * same as one whose author forgot to plot it, and the legend would change shape
 * between two creators the reader is comparing. `DonutChart` renders a zero
 * slice as no arc and a legend entry, which is exactly the reading wanted.
 */
export function safetyBandSlices(rows: readonly MentionRow[]): SliceRow[] {
  const tally = new Map<string, number>();
  for (const row of rows) {
    if (row.safetyBand === null) continue;
    tally.set(row.safetyBand, (tally.get(row.safetyBand) ?? 0) + 1);
  }
  return SAFETY_BANDS.map((band) => ({
    name: SAFETY_BAND_LABELS[band],
    value: tally.get(band) ?? 0,
  }));
}

/**
 * The `Audience reactions` ring — the four interaction types the corpus already
 * guarantees sum to `engagement`.
 *
 * That guarantee is why this card can print the engagement total in the middle
 * of the ring and have it be the sum of the slices rather than a second,
 * separately-computed figure that drifts from them by a few counts.
 */
export function reactionSlices(rows: readonly MentionRow[]): SliceRow[] {
  let reactions = 0;
  let comments = 0;
  let shares = 0;
  let likes = 0;
  for (const row of rows) {
    reactions += row.reactions;
    comments += row.comments;
    shares += row.shares;
    likes += row.likes;
  }
  return [
    { name: 'Reactions', value: reactions },
    { name: 'Comments', value: comments },
    { name: 'Shares', value: shares },
    { name: 'Likes', value: likes },
  ];
}

/** The `Sentiment breakdown` ring, in the chips' own order so the colours hold. */
export function sentimentSlices(rows: readonly MentionRow[]): SliceRow[] {
  const order = ['positive', 'neutral', 'negative'] as const;
  const labels: Record<string, string> = {
    positive: 'Positive',
    neutral: 'Neutral',
    negative: 'Negative',
  };
  const tally = new Map<string, number>();
  for (const row of rows) tally.set(row.sentiment, (tally.get(row.sentiment) ?? 0) + 1);
  return order.map((value) => ({ name: labels[value], value: tally.get(value) ?? 0 }));
}

/* ----------------------------------------------------------- formatting */

const compact = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 2 });
const whole = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

/**
 * `1_280_000` → `1.28M`, matching the frame's own column exactly.
 *
 * Two fraction digits rather than `metrics`' one, and that is the difference
 * between reproducing the design and not: `maximumFractionDigits: 1` renders
 * `1.28M` as `1.3M`. Follower counts are the only figures in the app quoted to
 * that precision, so this stays local rather than moving the shared formatter.
 */
export const formatFollowers = (value: number): string => compact.format(value);

export const formatCount = (value: number): string => whole.format(value);

/** A percentage, or an em dash when the period has nothing to compute it from. */
export const formatPercent = (value: number | null): string =>
  value === null ? NONE : `${value.toFixed(1)}%`;

/** Brand safety is whole percentage points — it is a score, not a measurement. */
export const formatScore = (value: number | null): string =>
  value === null ? NONE : `${Math.round(value)}%`;

export { NONE };
